import { createApi } from "@reduxjs/toolkit/query/react";

import { createAuthBaseQuery } from "../../common/utils";
import { HOSTNAME } from "../../constant/host";

// マイルストーンの型定義
export interface Milestone {
  event: string;
  date: string;
}

// ショットタグの型定義
export interface ShotTag {
  id: string;
  name: string;
  sort_rule: "newest" | "name_asc";
  position: number;
}

// ショットタグ更新用（id 付き=既存更新、id なし=新規作成）
export type ShotTagInput = {
  id?: string;
  name: string;
  sort_rule: "newest" | "name_asc";
};

// 認証ユーザーのプロジェクト内ロール
export type CurrentUserRole =
  | "CLIENT"
  | "REVIEWER"
  | "PRODUCER"
  | "DIRECTOR"
  | "ADMIN"
  | "SPECIALIST";

// タスクボードへの有効アクセスレベル（更新可 / 閲覧のみ / 共有しない）
export type TaskBoardAccess = "edit" | "view" | "none";

// 一覧 API がアバタースタック描画用に返す軽量メンバー情報
export interface MemberSummary {
  user_id: number;
  position: string;
  display_name_jp: string;
  display_name_en?: string;
  avatar_url: string | null;
  order_user: number;
  is_invited: boolean;
}

// プロジェクトオブジェクトの型定義（新 API レスポンスに準拠）
export interface Project {
  project_id: string;
  updated_at: string;
  code_name: string;
  final_work: string;
  rating: number;
  overview: string;
  end_client: string;
  start_date: string | null;
  end_date: string | null;
  total_shots_count: number;
  submitted_shots_count: number;
  approved_shots_count: number;
  pending_shots_count: number;
  banner_image_optimized: string | null;
  /**
   * banner_font: "1"〜"6" のフォントコード、または特殊値 "logo"
   * （banner_logo をコードネーム文字列の代わりに表示する）。
   */
  banner_font: string;
  banner_color: string;
  /** AI 生成のプロジェクトロゴ（白単色・透過 PNG の URL）。FE で tint 表示する */
  banner_logo: string | null;
  milestones: Milestone[];

  // 一覧 API でカード描画に必要なサマリ情報（BE 拡張により追加）
  current_user_role: CurrentUserRole;
  member_summaries: MemberSummary[];
  todo_count: number;
  unread_message_count: number;
  undownload_file_count: number;
  credit_count: number;

  // タスクボードに対する自分の有効アクセス（一覧でのフィルタ・編集可否に使う）
  task_board_access: TaskBoardAccess;
}

// ページネーション付きプロジェクト一覧レスポンス
export interface ProjectsData {
  projects: Project[];
  count: number;
  has_next: boolean;
  next_offset: number | null;
  limit: number;
  offset: number;
  // BE 拡張により追加: タブの存在判定を別 API なしに行うためのトータル件数
  total_active_count: number;
  total_completed_count: number;
  /** 完了タブの年チップ用。完了プロジェクトの年ごとの件数（年の新しい順） */
  completed_year_counts?: CompletedYearCount[];
}

export interface CompletedYearCount {
  year: number;
  count: number;
}

export interface ProjectsListResponse {
  status: string;
  data: ProjectsData;
}

/**
 * 進行中プロジェクト一覧の取得件数。ホイール表示は「新しいものだけ見えれば
 * よい」ため初回から大きめに取得する（足りない分は eager ページングで
 * 追い読みする）。プロジェクトリスト（Projects.tsx）とプロジェクト切り替え
 * ショートカット（GlobalShortcutListener）が同じ引数で購読して RTK Query の
 * キャッシュを共有するため、ここで一元管理する。
 */
export const ACTIVE_PROJECTS_LIMIT = 24;

// クエリパラメータの型
export type ProjectStatusFilter = "active" | "completed" | "all";
export type ProjectSortField = "last_update" | "created" | "name" | "rating";
export type SortOrder = "asc" | "desc";

export interface ProjectsQueryParams {
  status?: ProjectStatusFilter;
  search?: string;
  sort_by?: ProjectSortField;
  order?: SortOrder;
  limit?: number;
  offset?: number;
  /** 完了の年で絞り込む（終了日の年、未設定なら最終更新日の年） */
  year?: number;
}

// 詳細取得レスポンス（一覧とは構造が異なる: data.project でラップ）
export interface ProjectDetail extends Project {
  total_budget: number;
  allow_url_share: boolean;
  shot_tags: ShotTag[];
  /**
   * クライアントから直接スペシャリストへ委託するオファーを許可するかどうか。
   * オファーウィザードの表示可否判定で参照する（クライアントグループのみ参照される）。
   */
  // TODO(BE): フィールド名は仮置き。BE 実装が確定したら型と読み出し箇所をあわせて更新する。
  is_direct_subcontract_allowed?: boolean;

  // タスクボード共有設定（責任者プロデューサーが設定する生値）。設定UI で参照する。
  task_board_director_access: TaskBoardAccess;
  task_board_client_access: TaskBoardAccess;
  // 共有設定を変更できるか（責任者プロデューサー・管理者のみ true）
  task_board_can_manage: boolean;

  /** バナー画像の切り抜き前の元画像 URL（再クロップ用。未設定は null） */
  banner_image_source: string | null;
  /** 元画像に対する切り抜き座標（未設定は null） */
  banner_crop_data: BannerCropData | null;
}

/** 元画像に対する切り抜き座標。座標は元画像のピクセル */
export interface BannerCropData {
  x: number;
  y: number;
  width: number;
  height: number;
  /** 選んだ比率（幅 / 高さ）。null は「そのまま」 */
  aspect?: number | null;
}

export interface ProjectDetailResponse {
  status: string;
  data: {
    project: ProjectDetail;
  };
}

// 単一プロジェクト取得用
interface ProjectQueryParams {
  project_id: string;
}

// プロジェクト更新リクエスト（PATCH: 送信フィールドのみ更新）
interface UpdateProjectRequest {
  project_id: string;
  code_name?: string;
  final_work?: string;
  overview?: string;
  end_client?: string;
  banner_font?: string;
  banner_color?: string;
  /**
   * 切り抜き済みバナー画像の S3 file_key（purpose=project_banner）。
   * 空文字でバナーを外す（元画像と切り抜き座標も BE 側で消える）
   */
  banner_image_key?: string;
  /** 切り抜き前の元画像の S3 file_key。再クロップでは送らない（既存を残す） */
  banner_image_source_key?: string;
  /** 元画像に対する切り抜き座標 */
  banner_crop_data?: BannerCropData | null;
  /** プロジェクトロゴ（透過 PNG）の S3 file_key。空文字で解除 */
  banner_logo_key?: string;
  total_shots_count?: number;
  start_date?: string | null;
  end_date?: string | null;
  milestones?: Milestone[];
  shot_tags?: ShotTagInput[];
}

// プロジェクト新規作成リクエスト（POST: final_work 必須、その他は BE 側のデフォルトで補完）
export interface CreateProjectRequest {
  final_work: string;
  code_name?: string;
  overview?: string;
  end_client?: string;
  banner_font?: string;
  banner_color?: string;
  total_shots_count?: number;
  start_date?: string | null;
  end_date?: string | null;
  total_budget?: number;
  allow_url_share?: boolean;
  /**
   * 新決済モデル（PaymentLedger 連動）で運用するか。新 SPA から作成する場合は
   * 必ず true を送る（旧 SSR との両立のため BE 側のデフォルトは false）。
   */
  is_new_payment_model?: boolean;
  milestones?: Milestone[];
  shot_tags?: ShotTagInput[];
}

export const projectsApi = createApi({
  reducerPath: "projectsApi",
  baseQuery: createAuthBaseQuery(HOSTNAME),
  tagTypes: ["Projects"],
  endpoints: (builder) => ({
    getProjects: builder.query<ProjectsListResponse, ProjectsQueryParams>({
      query: ({
        status = "active",
        search,
        sort_by = "last_update",
        order = "desc",
        offset = 0,
        limit,
        year,
      }) => {
        const params: Record<string, string> = {
          status,
          sort_by,
          order,
          offset: offset.toString(),
        };

        if (search) {
          params.search = search;
        }

        if (year !== undefined) {
          params.year = year.toString();
        }

        if (limit !== undefined) {
          params.limit = limit.toString();
        }

        const queryString = new URLSearchParams(params).toString();
        return `/api/me/projects?${queryString}`;
      },
      providesTags: (result) =>
        result?.data?.projects
          ? [
              ...result.data.projects.map((p) => ({
                type: "Projects" as const,
                id: p.project_id,
              })),
              { type: "Projects", id: "LIST" },
            ]
          : [{ type: "Projects", id: "LIST" }],
      // 集計値も BE で同期されるため、戻り遷移時のキャッシュ即時表示を狙って 60 秒保持する。
      keepUnusedDataFor: 60,
    }),

    getProject: builder.query<ProjectDetailResponse, ProjectQueryParams>({
      query: ({ project_id }) => `/api/me/projects/${project_id}`,
      providesTags: (_result, _error, { project_id }) => [
        { type: "Projects", id: project_id },
      ],
    }),

    updateProject: builder.mutation<ProjectDetailResponse, UpdateProjectRequest>({
      query: ({ project_id, ...body }) => ({
        url: `/api/me/projects/${project_id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_result, _error, { project_id }) => [
        { type: "Projects", id: project_id },
        { type: "Projects", id: "LIST" },
      ],
    }),

    createProject: builder.mutation<ProjectDetailResponse, CreateProjectRequest>({
      query: (body) => ({
        url: "/api/me/projects",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "Projects", id: "LIST" }],
    }),
  }),
});

export const {
  useGetProjectsQuery,
  useLazyGetProjectsQuery,
  useGetProjectQuery,
  useUpdateProjectMutation,
  useCreateProjectMutation,
} = projectsApi;
